/**
 * Test seams — expose game state on `window.__GAME__` for Playwright assertions.
 *
 * Only active when VITE_PLAYWRIGHT=true (set by playwright.config.ts webServer).
 * In production builds this module is a no-op because Vite dead-code-eliminates
 * the `import.meta.env` branch.
 */
import type * as Phaser from 'phaser';
import { getC5Frame } from '../ui/c5/C5Frame';

export interface GameTestState {
  /** true once Phaser has booted and the first scene is running */
  booted: boolean;
  /** key of the currently active scene (e.g. 'OverworldScene', 'GameScene') */
  activeScene: string | null;
  /** local player world position, null until spawned */
  player: { x: number; y: number } | null;
  /** Colyseus connection status */
  connected: boolean;
  /** number of remote players visible */
  remotePlayers: number;
  /** number of NPCs visible */
  npcCount: number;
  /** true while dialogue UI is showing */
  dialogueOpen: boolean;
  /** current scene's key list */
  sceneKeys: string[];
  /** incremented every frame the E (interact) key was JustDown inside the
   *  OverworldScene update loop — BEFORE any suppression branch. Used by
   *  smoke tests to verify the interact key is reaching the scene's input
   *  handler at all, independent of whether an NPC is nearby. */
  interactKeyDowns: number;
  /** incremented every time OverworldScene actually dispatches
   *  MSG_NPC_INTERACT to the server (i.e. E pressed + near an NPC +
   *  dialogue not already active). */
  npcInteractsSent: number;
  /** true when the C5 "Indigo & Ink" DOM frame is mounted (?c5=1). */
  c5UiActive: boolean;
}

declare global {
  interface Window {
    __GAME__?: GameTestState;
    __PHASER__?: Phaser.Game;
  }
}

const isPlaywright = (import.meta as unknown as { env?: Record<string, string> })
  .env?.VITE_PLAYWRIGHT === 'true';

/**
 * Install test seams on the Phaser game instance.
 * Call this right after `new Phaser.Game(config)`.
 */
export function installTestSeams(game: Phaser.Game): void {
  if (!isPlaywright) return;

  const state: GameTestState = {
    booted: false,
    activeScene: null,
    player: null,
    connected: false,
    remotePlayers: 0,
    npcCount: 0,
    dialogueOpen: false,
    sceneKeys: [],
    interactKeyDowns: 0,
    npcInteractsSent: 0,
    c5UiActive: getC5Frame() !== null,
  };

  window.__GAME__ = state;
  window.__PHASER__ = game;

  game.events.once('ready', () => {
    state.booted = true;
  });
}

/**
 * Update test seam state from inside a scene's update() loop.
 * Scenes call this each frame so Playwright always sees fresh data.
 */
export function updateTestSeams(opts: {
  sceneKey: string;
  playerX?: number;
  playerY?: number;
  connected: boolean;
  remotePlayers: number;
  npcCount: number;
  dialogueOpen: boolean;
}): void {
  if (!isPlaywright || !window.__GAME__) return;

  const s = window.__GAME__;
  s.activeScene = opts.sceneKey;
  s.connected = opts.connected;
  s.remotePlayers = opts.remotePlayers;
  s.npcCount = opts.npcCount;
  s.dialogueOpen = opts.dialogueOpen;

  if (opts.playerX !== undefined && opts.playerY !== undefined) {
    s.player = { x: opts.playerX, y: opts.playerY };
  }
}

/** Called once per frame when the scene's interact key was JustDown. Lets
 *  smoke tests verify the keystroke reached the scene even if no NPC was
 *  nearby (the suppression branches run after this increment). */
export function recordInteractKeyDown(): void {
  if (!isPlaywright || !window.__GAME__) return;
  window.__GAME__.interactKeyDowns += 1;
}

/** Called when OverworldScene actually dispatches MSG_NPC_INTERACT. */
export function recordNpcInteractSent(): void {
  if (!isPlaywright || !window.__GAME__) return;
  window.__GAME__.npcInteractsSent += 1;
}
